// Soft contact shadows under the balls, instead of the spot light's shadow map (which drew blocky, pixelated
// shadows and cost a second render pass of every ball plus shadow lookups on the cloth, cushions and rails).
//
// Each ball on the table gets two blurred dark blobs on the cloth: a small dense core right under it (the contact)
// and a larger, fainter one pushed a little away from the lamp over the table centre and stretched along that
// direction. All of them are quads in one dynamic mesh whose shader draws a radial falloff: one draw call for
// every ball, no texture. A potted ball has none; a ball in a pocket's jaws fades as it goes over the opening.
// The placement maths (shadowQuads) is pure, for ballShadows.test.ts.
import {BALL_RADIUS, TABLE, Vec} from "./shared/rules";
import {BED_Y, outsideCushions} from "./tableShapes";
import {LAMP} from "./roomLayout";

declare const THREE: any;

/** Height of the blobs above the cloth (above the markings, which are at +0.002). */
export const SHADOW_LIFT = 0.004;
const CORE = {radius: 1.3 * BALL_RADIUS, alpha: 0.75};
const SOFT = {radius: 2.1 * BALL_RADIUS, alpha: 0.5, offset: 0.35 * BALL_RADIUS, stretch: 0.32};

export interface ShadowQuad {
    /** corners in the physics plane, counter-clockwise */
    corners: [Vec, Vec, Vec, Vec];
    alpha: number;
}

/** How much of a ball's shadow shows: 1 on the bed, fading to 0.15 as its centre goes past the cushion line. */
export function shadowStrength(p: Vec): number {
    const t = Math.max(0, Math.min(1, outsideCushions(p) / (1.2 * BALL_RADIUS)));
    return 1 - 0.85 * t;
}

/** The two quads (core, soft) for a ball at p on the table. */
export function shadowQuads(p: Vec): ShadowQuad[] {
    const k = shadowStrength(p);
    // away from the lamp over the table: further from the centre, further the shadow is pushed and stretched
    let dx = p[0] - LAMP.cx, dz = p[1] - LAMP.cz;
    const d = Math.hypot(dx, dz);
    const reach = Math.min(1, d / Math.hypot(TABLE.xMax - LAMP.cx, TABLE.yMax - LAMP.cz));
    if (d < 1e-6) { dx = 0; dz = 1; } else { dx /= d; dz /= d; }
    const quad = (cx: number, cz: number, along: number, across: number): [Vec, Vec, Vec, Vec] => {
        // axes: along = (dx, dz), across = (-dz, dx)
        const ax = dx * along, az = dz * along, bx = -dz * across, bz = dx * across;
        return [[cx - ax - bx, cz - az - bz], [cx + ax - bx, cz + az - bz], [cx + ax + bx, cz + az + bz], [cx - ax + bx, cz - az + bz]];
    };
    const soft = SOFT.offset * (0.4 + 0.6 * reach);
    return [
        {corners: quad(p[0], p[1], CORE.radius, CORE.radius), alpha: CORE.alpha * k},
        {corners: quad(p[0] + dx * soft, p[1] + dz * soft, SOFT.radius * (1 + SOFT.stretch * reach), SOFT.radius), alpha: SOFT.alpha * k},
    ];
}

const QUADS_PER_BALL = 2;

const VERTEX = [
    "attribute vec3 blob;",
    "varying float vAlpha;",
    "varying vec2 vUv;",
    "void main() {",
    "  vUv = blob.xy;",
    "  vAlpha = blob.z;",
    "  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);",
    "}",
].join("\n");

// The radial falloff is computed in the shader rather than sampled from a texture: no texture
// upload, nothing to filter. The quad coordinates and strength share one custom attribute ("blob"), so
// the mesh needs no uv buffer. The falloff starts at 30% of the radius: seen from above, most of the
// blob is hidden under the ball, and only the ring outside it shows.
const FRAGMENT = [
    "varying float vAlpha;",
    "varying vec2 vUv;",
    "void main() {",
    "  float d = length(vUv - vec2(0.5)) * 2.0;",
    "  float a = 1.0 - smoothstep(0.3, 1.0, d);",
    "  gl_FragColor = vec4(0.0, 0.0, 0.0, a * vAlpha);",
    "}",
].join("\n");

export class BallShadows {
    mesh: any;
    private geometry: any;
    private blob: any[];
    private material: any;

    constructor(readonly count: number) {
        const g = new THREE.Geometry();
        const quads = count * QUADS_PER_BALL;
        this.blob = [];
        for (let q = 0; q < quads; q++) {
            const base = q * 4;
            const uv = [[0, 0], [1, 0], [1, 1], [0, 1]];
            for (let k = 0; k < 4; k++) {
                g.vertices.push(new THREE.Vector3(0, -1000, 0));
                this.blob.push(new THREE.Vector3(uv[k][0], uv[k][1], 0));
            }
            // the corners go counter-clockwise in the physics plane (x, z), which is clockwise seen from above:
            // wind the faces the other way so they face up
            g.faces.push(new THREE.Face3(base, base + 2, base + 1));
            g.faces.push(new THREE.Face3(base, base + 3, base + 2));
        }
        g.dynamic = true;
        if (g.computeCentroids) g.computeCentroids();
        g.computeFaceNormals();
        this.geometry = g;
        this.material = new THREE.ShaderMaterial({
            uniforms: {},
            attributes: {blob: {type: "v3", value: this.blob}},
            vertexShader: VERTEX,
            fragmentShader: FRAGMENT,
            transparent: true,
            depthWrite: false,
            polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4,
        });
        this.mesh = new THREE.Mesh(g, this.material);
        this.mesh.frustumCulled = false;
        this.mesh.matrixAutoUpdate = false;
        this.mesh.name = "ball shadows";
    }

    /** Once per frame: positions of the balls on the table (potted balls are left out). */
    update(balls: {x: number, z: number}[]) {
        const v = this.geometry.vertices;
        const y = BED_Y + SHADOW_LIFT;
        for (let i = 0; i < this.count; i++) {
            const quads = i < balls.length ? shadowQuads([balls[i].x, balls[i].z]) : null;
            for (let q = 0; q < QUADS_PER_BALL; q++) {
                const base = (i * QUADS_PER_BALL + q) * 4;
                for (let k = 0; k < 4; k++) {
                    if (quads) {
                        const c = quads[q].corners[k];
                        v[base + k].set(c[0], y, c[1]);
                        this.blob[base + k].z = quads[q].alpha;
                    } else {
                        v[base + k].set(0, -1000, 0);
                        this.blob[base + k].z = 0;
                    }
                }
            }
        }
        this.geometry.verticesNeedUpdate = true;
        this.material.attributes.blob.needsUpdate = true;
    }
}
